import type { PermissionsRolesEnum } from "@/enums"

import PERMISSION_ROLES from "./permission-roles"

// Shape de cada rota do front.
// - requireSuperAdmin: exclusivo staff Pay2B (email @pay2b.io).
// - requireAdmin: admin do merchant (passa com isAdmin || isSuperAdmin).
export interface FrontEndRoute {
  label: string
  href: string
  requireSuperAdmin: boolean
  requireAdmin: boolean
  permissionRoles: readonly PermissionsRolesEnum[]
}

const FRONTEND_ROUTES = {
  DASHBOARD: {
    label: "Dashboard",
    href: "/dashboard",
    requireSuperAdmin: false,
    requireAdmin: false,
    permissionRoles: PERMISSION_ROLES.DASHBOARD,
  },
  DEFAULTERS: {
    label: "Inadimplentes",
    href: "/defaulters",
    requireSuperAdmin: false,
    requireAdmin: false,
    permissionRoles: PERMISSION_ROLES.DEFAULTERS,
  },
  ORDERS: {
    label: "Pedidos",
    href: "/orders",
    requireSuperAdmin: false,
    requireAdmin: false,
    permissionRoles: PERMISSION_ROLES.ORDERS,
  },
  SALES: {
    label: "Vendas",
    href: "/sales",
    requireSuperAdmin: false,
    requireAdmin: false,
    permissionRoles: PERMISSION_ROLES.SALES,
  },
  SUBSCRIPTIONS: {
    label: "Assinaturas",
    href: "/subscriptions",
    requireSuperAdmin: false,
    requireAdmin: false,
    permissionRoles: PERMISSION_ROLES.SUBSCRIPTIONS,
  },
  SCHEDULE: {
    label: "Schedule",
    href: "/schedule",
    requireSuperAdmin: false,
    requireAdmin: false,
    permissionRoles: PERMISSION_ROLES.SCHEDULE,
  },
  ACCESS_MANAGEMENT: {
    label: "Gestão de acessos",
    href: "/access-management",
    requireSuperAdmin: false,
    requireAdmin: true,
    permissionRoles: PERMISSION_ROLES.ACCESS_MANAGEMENT,
  },
  LOGIN: {
    label: "Login",
    href: "/",
    requireSuperAdmin: false,
    requireAdmin: false,
    permissionRoles: PERMISSION_ROLES.LOGIN,
  },
  FORGOT_PASSWORD: {
    label: "Esqueci minha senha",
    href: "/forgot-password",
    requireSuperAdmin: false,
    requireAdmin: false,
    permissionRoles: PERMISSION_ROLES.FORGOT_PASSWORD,
  },
  SSO_LOGIN_FORM: {
    label: "Login com provedor externo",
    href: "/sso-login-form",
    requireSuperAdmin: false,
    requireAdmin: false,
    permissionRoles: PERMISSION_ROLES.SSO_LOGIN_FORM,
  },
  FIRST_LOGIN: {
    label: "Primeiro acesso",
    href: "/first-login",
    requireSuperAdmin: false,
    requireAdmin: false,
    permissionRoles: PERMISSION_ROLES.FIRST_LOGIN,
  },
  RECOVERY_PASSWORD: {
    label: "Recuperar senha",
    href: "/recovery-password",
    requireSuperAdmin: false,
    requireAdmin: false,
    permissionRoles: PERMISSION_ROLES.RECOVERY_PASSWORD,
  },
  NOTIFICATION_CENTER: {
    label: "Central de notificações",
    href: "/notification-center",
    requireSuperAdmin: false,
    requireAdmin: true,
    permissionRoles: PERMISSION_ROLES.NOTIFICATION_CENTER,
  },
  NOTIFICATION_CENTER_SEARCH: {
    label: "Consulta",
    href: "/notification-center/search",
    requireSuperAdmin: false,
    requireAdmin: true,
    permissionRoles: PERMISSION_ROLES.NOTIFICATION_CENTER,
  },
  NOTIFICATION_CENTER_CONFIGURATIONS: {
    label: "Configurações",
    href: "/notification-center/configurations",
    requireSuperAdmin: false,
    requireAdmin: true,
    permissionRoles: PERMISSION_ROLES.NOTIFICATION_CENTER,
  },
  NOTIFICATION_CENTER_TEMPLATES: {
    label: "Templates",
    href: "/notification-center/templates",
    requireSuperAdmin: false,
    requireAdmin: true,
    permissionRoles: PERMISSION_ROLES.NOTIFICATION_CENTER,
  },
  WEBHOOKS: {
    label: "Webhooks",
    href: "/webhooks",
    requireSuperAdmin: false,
    requireAdmin: false,
    permissionRoles: PERMISSION_ROLES.WEBHOOKS,
  },
  REPIC_CENTER: {
    label: "Repic center",
    href: "/repic-center",
    requireSuperAdmin: false,
    requireAdmin: true,
    permissionRoles: PERMISSION_ROLES.REPIC_CENTER,
  },
  CUSTOMERS: {
    label: "Clientes",
    href: "/customers",
    requireSuperAdmin: false,
    requireAdmin: false,
    permissionRoles: PERMISSION_ROLES.CUSTOMERS,
  },
  SMART_ROUTING: {
    label: "Smart Routing",
    href: "/smart-routing",
    requireSuperAdmin: false,
    requireAdmin: true,
    permissionRoles: PERMISSION_ROLES.SMART_ROUTING,
  },
  SMART_ROUTING_CONFIGURATIONS: {
    label: "Configurações",
    href: "/smart-routing/configurations",
    requireSuperAdmin: false,
    requireAdmin: true,
    permissionRoles: PERMISSION_ROLES.SMART_ROUTING,
  },
  SMART_ROUTING_DASHBOARD: {
    label: "Dashboard",
    href: "/smart-routing/dashboard",
    requireSuperAdmin: false,
    requireAdmin: true,
    permissionRoles: PERMISSION_ROLES.SMART_ROUTING,
  },
  SMART_ROUTING_CONFIGURATION_CREATE: {
    label: "Criar Configuração",
    href: "/smart-routing/configurations/new",
    requireSuperAdmin: false,
    requireAdmin: true,
    permissionRoles: PERMISSION_ROLES.SMART_ROUTING,
  },
  SMART_ROUTING_CONFIGURATION_EDIT: {
    label: "Editar Configuração",
    href: "/smart-routing/configurations/:uuid",
    requireSuperAdmin: false,
    requireAdmin: true,
    permissionRoles: PERMISSION_ROLES.SMART_ROUTING,
  },
  ADMIN_REPROCESS: {
    label: "Reprocessar Pagamentos",
    href: "/admin/reprocess",
    requireSuperAdmin: true,
    requireAdmin: false,
    permissionRoles: PERMISSION_ROLES.ADMIN_REPROCESS,
  },
  // As credenciais do merchant. "Processadores" passou a nomear ESTA tela: a
  // antiga (que habilita metodo por white label) virou "Meios de pagamento".
  // Detalhe do processador: pagina propria, nao gaveta. O que se faz aqui —
  // consolidar credencial duplicada movendo vinculo a vinculo — nao cabe num
  // painel lateral, e precisa de URL para o operador voltar e compartilhar.
  ADMIN_MERCHANT_PROCESSOR_DETAIL: {
    label: "Processador",
    href: "/admin/processors/:processorId",
    requireSuperAdmin: true,
    requireAdmin: false,
    permissionRoles:
      PERMISSION_ROLES.ADMIN_PROCESSOR_CONFIG,
  },
  // Detalhe do meio de pagamento, tambem em pagina propria.
  ADMIN_PAYMENT_METHOD_DETAIL: {
    label: "Meio de pagamento",
    href: "/admin/payment-methods/:configId",
    requireSuperAdmin: true,
    requireAdmin: false,
    permissionRoles:
      PERMISSION_ROLES.ADMIN_PROCESSOR_CONFIG,
  },
  ADMIN_MERCHANT_PROCESSORS: {
    label: "Processadores",
    href: "/admin/processors",
    requireSuperAdmin: true,
    requireAdmin: false,
    permissionRoles:
      PERMISSION_ROLES.ADMIN_PROCESSOR_CONFIG,
  },
  // Fora do menu de propósito: a porta de entrada é a aba de métodos do white
  // label, que é onde o operador já está quando pergunta "quem processa isto?".
  // A rota continua existindo — é ela que os links de lá abrem.
  ADMIN_PROCESSOR_CONFIG: {
    label: "Meios de pagamento",
    href: "/admin/processor-config",
    requireSuperAdmin: true,
    requireAdmin: false,
    permissionRoles:
      PERMISSION_ROLES.ADMIN_PROCESSOR_CONFIG,
  },
  ADMIN_WHITE_LABELS: {
    label: "White Labels",
    href: "/admin/white-labels",
    requireSuperAdmin: true,
    requireAdmin: false,
    permissionRoles: PERMISSION_ROLES.ADMIN_WHITE_LABELS,
  },
  ADMIN_WHITE_LABEL_DETAIL: {
    label: "Detalhe do White Label",
    href: "/admin/white-labels/:uuid",
    requireSuperAdmin: true,
    requireAdmin: false,
    permissionRoles: PERMISSION_ROLES.ADMIN_WHITE_LABELS,
  },
  // Operação de webhooks recebidos dos processadores (payment). Fica no fim do
  // grupo por ser diagnóstico do dia a dia, não cadastro.
  ADMIN_WEBHOOK_INBOX: {
    label: "Webhook Inbox",
    href: "/webhook-inbox",
    requireSuperAdmin: true,
    requireAdmin: false,
    permissionRoles: PERMISSION_ROLES.ADMIN_WEBHOOK_INBOX,
  },
  ADMIN_MERCHANT: {
    label: "Ficha do Merchant",
    href: "/admin/merchant",
    requireSuperAdmin: true,
    requireAdmin: false,
    permissionRoles: PERMISSION_ROLES.ADMIN_MERCHANTS,
  },
} as const satisfies Record<string, FrontEndRoute>
export default FRONTEND_ROUTES
